import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ExternalLink, Shield, Building2, Handshake } from 'lucide-react';

// Source unique : public/partenaires.json (également consommé par ATOMIZER).
// Les icônes ne sont pas sérialisables en JSON, on les associe ici par clé de catégorie.
const CATEGORY_ICONS = {
  assurances: Shield,
  banques: Building2,
  associations: Handshake
};

// Composant pour afficher un logo ou un placeholder
const PartnerLogo = ({ partner }) => {
  const [imageError, setImageError] = React.useState(false);
  
  if (!partner.logo || imageError) {
    const initials = partner.name
      .split(' ')
      .filter(word => word.length > 2 && !['SA', 'AG', 'de', 'la', 'sur', 'et', "d'", 'Société'].includes(word))
      .slice(0, 2)
      .map(word => word[0])
      .join('')
      .toUpperCase();
    
    return (
      <div className="w-full h-16 flex items-center justify-center">
        <span className="text-2xl font-bold text-blue-300">
          {initials || partner.name.substring(0, 2).toUpperCase()}
        </span>
      </div>
    );
  }
  
  return (
    <div className="w-full h-16 flex items-center justify-center">
      <img
        src={partner.logo}
        alt={`Logo ${partner.name}`}
        className="max-h-full max-w-full object-contain rounded-lg"
        style={{ maxHeight: '64px' }}
        onError={() => setImageError(true)}
      />
    </div>
  );
};

// Composant carte partenaire
const PartnerCard = ({ partner }) => {
  return (
    <a
      href={partner.website}
      target="_blank"
      rel="noopener noreferrer"
      className="group block"
    >
      <div className="glass p-6 h-full transition-all duration-300 group-hover:scale-[1.02]">
        {/* Logo */}
        <PartnerLogo partner={partner} />
        
        {/* Nom */}
        <h3 className="mt-4 text-lg font-semibold text-white group-hover:text-blue-300 transition-colors line-clamp-2">
          {partner.name}
        </h3>
        
        {/* Lien */}
        <div className="mt-4 flex items-center text-blue-400 text-sm font-medium group-hover:text-blue-300">
          <span>Visiter le site</span>
          <ExternalLink className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </a>
  );
};

const Partenaires = () => {
  const [categories, setCategories] = React.useState([]);
  const [heroStats, setHeroStats] = React.useState({ partners: '45+', swiss: '100%' });

  React.useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.PUBLIC_URL || ''}/assets/partners/partenaires.json`, { cache: 'no-cache' })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (cancelled) return;
        setCategories(Array.isArray(data.categories) ? data.categories : []);
        if (data.hero_stats) setHeroStats(prev => ({ ...prev, ...data.hero_stats }));
      })
      .catch(err => console.error('Chargement des partenaires impossible:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="pt-20">
      <Helmet>
        <title>Nos Partenaires | ATOM Group - Conseil Financier Suisse</title>
        <meta name="description" content="Découvrez nos partenaires de confiance : compagnies d'assurances, caisses de pension et institutions financières suisses avec lesquelles ATOM Group collabore." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900/50 to-slate-900"></div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold heading-display text-white mb-6">
              Nos <span className="text-blue-300">Partenaires</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              ATOM Group collabore avec les meilleures compagnies d'assurances et institutions financières 
              suisses pour vous offrir des solutions optimales et personnalisées.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div className="glass p-6 text-center">
                <div className="text-3xl font-bold text-blue-300">{heroStats.partners}</div>
                <div className="text-sm text-gray-300 mt-1">Partenaires</div>
              </div>
              <div className="glass p-6 text-center">
                <div className="text-3xl font-bold text-blue-300">{heroStats.swiss}</div>
                <div className="text-sm text-gray-300 mt-1">Suisses</div>
              </div>
              <div className="glass p-6 text-center">
                <div className="text-3xl font-bold text-blue-300">∞</div>
                <div className="text-sm text-gray-300 mt-1">Indépendance</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Sections */}
      {categories.map(category => {
        const IconComponent = CATEGORY_ICONS[category.key] || Handshake;
        return (
          <section key={category.key} className="py-20">
            <div className="container">
              {/* Category Header */}
              <div className="text-center mb-16">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <IconComponent size={32} className="text-white" />
                </div>
                <h2 className="text-4xl font-bold heading-display text-white mb-4">
                  {category.title}
                </h2>
                <p className="text-xl text-gray-300">
                  {category.partners.length} partenaires de confiance
                </p>
              </div>

              {/* Partners Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {category.partners.map((partner, index) => (
                  <PartnerCard key={index} partner={partner} />
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA Section */}
      <section className="py-20">
        <div className="container">
          <div className="glass p-12 text-center max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold heading-display text-white mb-4">
              Vous souhaitez devenir partenaire ?
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Rejoignez notre réseau de partenaires de confiance et développons ensemble 
              des solutions financières innovantes pour nos clients.
            </p>
            <a
              href="/contact"
              className="btn-glass btn-primary text-lg px-8 py-4 inline-flex items-center"
            >
              <Building2 className="w-5 h-5 mr-2" />
              Nous contacter
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partenaires;